import {useForm, Yup} from "../forms/form";
import isEmpty from "lodash/isEmpty";
import { toast } from "react-toastify";
import React from "react";
import styled from "styled-components";

const ToastUl = styled.ul`
  list-style-type: none;
  margin: 0;
  padding: 0;
`

const ToastLi = styled.li`
  text-indent: -20px;
  margin-left: 20px;
`

export const authorizedEventTypes = [
  "locations",
  "traces",
  "visits",
  "geovisits",
  "snapped_traces",
  "wifi_events",
  "beacons",
]

export function useDataProductForm(props) {

  const hasZoneFilter = (obj) => {
    return !isEmpty(obj.zone) || !isEmpty(obj.zone_ids)
  }

  const extraInitialValues = props.initialValues !== undefined ? props.initialValues : {};

  const initialValues = {
    ...extraInitialValues,
    name: '',
    start_date: '',
    end_date: '',
    stream: '',
    idfa_required: false,
    os: '',
    home_required: (obj) => obj?.home?.required || false,
    work_required: (obj) => obj?.work?.required || false,
    data_type: '',
    event_filter: (obj) => !isEmpty(obj) ? hasZoneFilter(obj) : false,
    home_filter: (obj) => !isEmpty(obj?.home) ? hasZoneFilter(obj.home) : false,
    work_filter: (obj) => !isEmpty(obj?.work) ? hasZoneFilter(obj.work) : false,
    use_existing_event_zone: (obj) => !isEmpty(obj) ? !isEmpty(obj.zone_ids) : '',
    use_existing_home_zone: (obj) => !isEmpty(obj?.home) ? !isEmpty(obj.home.zone_ids) : '',
    use_existing_work_zone: (obj) => !isEmpty(obj?.work) ? !isEmpty(obj.work.zone_ids) : '',
    event_zone_ids: (obj) => obj?.zone_ids || [],
    home_zone_ids: (obj) => obj?.home?.zone_ids || [],
    work_zone_ids: (obj) => obj?.work?.zone_ids || [],
    event_zone: (obj) => obj?.zone || {},
    home_zone: (obj) => obj?.home?.zone || {},
    work_zone: (obj) => obj?.work?.zone || {},
    traces_zone_type: '',
    api_environment: 'production',
    column_mapping: null,
    custom_filters: null,
    publisher_filters: {}
  }

  const validationSchema = Yup.object().shape({
    name: Yup.string(),
    data_type: Yup.string().oneOf(authorizedEventTypes).required(),
    start_date: Yup.string(),
    end_date: Yup.string(),
    stream: Yup.string(),
    idfa_required: Yup.bool().required(),
    os: Yup.string().oneOf(['ios', 'android', '', null]).nullable(),
    home_required: Yup.bool().required(),
    work_required: Yup.bool().required(),
    event_filter: Yup.bool().required(),
    use_existing_event_zone: Yup.mixed().when('event_filter', {
      is: true,
      then: Yup.bool().required(),
    }),
    home_filter: Yup.bool().required(),
    use_existing_home_zone: Yup.mixed().when('home_filter', {
      is: true,
      then: Yup.bool().required(),
    }),
    work_filter: Yup.bool().required(),
    use_existing_work_zone: Yup.mixed().when('work_filter', {
      is: true,
      then: Yup.bool().required(),
    }),
    event_zones: Yup.array(Yup.string()).when('use_existing_event_zone', {
      is: true,
      then: Yup.array().min(1)
    }),
    home_zones: Yup.array(Yup.string()).when('use_existing_home_zone', {
      is: true,
      then: Yup.array().min(1)
    }),
    work_zones: Yup.array(Yup.string()).when('use_existing_work_zone', {
      is: true,
      then: Yup.array().min(1)
    }),
    traces_zone_type: Yup.mixed().when('traces', {
      is: true,
      then: Yup.string().required(),
    })
  })

  const zoneAttributes = (useZone, zoneIds, zone) => ( useZone ? {zone_ids: zoneIds} : {zone: zone} );

  const processedDataForSubmit = (data) => {
    const {event_filter, use_existing_event_zone, home_filter, use_existing_home_zone, work_filter,
      use_existing_work_zone, event_zone_ids, home_zone_ids, work_zone_ids, event_zone, home_zone, work_zone,
      home_required, work_required, ...filteredData} = data
    authorizedEventTypes.forEach(type => delete filteredData[type])

    let zones_attributes = {events: {}, home: {}, work: {}}
    if (event_filter) {
      zones_attributes.events = zoneAttributes(use_existing_event_zone, event_zone_ids, event_zone)
    }
    if (home_filter) {
      zones_attributes.home = zoneAttributes(use_existing_home_zone, home_zone_ids, home_zone)
    }
    if (work_filter) {
      zones_attributes.work = zoneAttributes(use_existing_work_zone, work_zone_ids, work_zone)
    }

    return {
      ...filteredData,
      ...zones_attributes.events,
      home: {
        required: home_required,
        ...zones_attributes.home
      },
      work: {
        required: work_required,
        ...zones_attributes.work
      }
    }
  }

  const errorLayout = (errors) => {
    let errorList = []
    for (const key in errors) {
      for (const error of errors[key]) {
        errorList.push(`${error}`);
      }
    }
    return (<ToastUl>
      { errorList.map((e, i)=>(
        <ToastLi key={i}>{e}</ToastLi>
      )) }
    </ToastUl>)
  }

  let useFormAttributes = {
    defaultObject: null,
    initialValues: initialValues,
    validationSchema: props.validationSchema === undefined ? validationSchema : validationSchema.concat(props.validationSchema),
    onSubmit: (data) => props.onSubmit(processedDataForSubmit(data)),
    disabled: props.disabled,
    setSubmitButtonProps: props.setSubmitButtonProps,
    errors: props.errors,
    onValidationFail: (errors) => (toast.error(errorLayout(errors)))
  }

  if ( ! props.unbindBucket ) {
    useFormAttributes = {
      ...useFormAttributes,
      onAttributeChange: {
        name: {
          stream: (value) => {
            return value.toLowerCase() === 'default' ? 'root' : value.toLowerCase().replace(/\s/g,'_').replace(/[^a-z0-9-_]/g,'')
          }
        }
      },
    }
  }

  return useForm(useFormAttributes)
}
